/**
 * Affichage ligne : prix fort + part promo au prorata (pédagogique).
 * Le remboursement I1 reste piloté par ancienTotal − nouveauTotal côté API.
 */

function roundMoney(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Montant TTC brut de la ligne (catalogue / snapshot).
 * Après ajustement poids, prixFinal est la source de vérité — prioritaire sur le snapshot.
 */
export function resolveLigneBrutTTC(ligne) {
  if (ligne?.prixFinal != null && ligne.prixFinal !== '') {
    const n = Number(ligne.prixFinal);
    if (Number.isFinite(n)) return roundMoney(Math.max(0, n));
  }
  if (ligne?.montantTTCSnapshot != null && ligne.montantTTCSnapshot !== '') {
    const n = Number(ligne.montantTTCSnapshot);
    if (Number.isFinite(n)) return roundMoney(Math.max(0, n));
  }
  const n = Number(ligne?.prixTotal);
  if (Number.isFinite(n)) return roundMoney(Math.max(0, n));
  const fallback =
    (Number(ligne?.prixUnitaire) || 0) * (Number(ligne?.quantite) || 0);
  return roundMoney(Math.max(0, fallback));
}

/**
 * @param {object} ligne
 * @param {{ reductionOrig?: number, sousTotalOriginal?: number }} opts
 * @returns {{ brut: number, net: number, partPromo: number, hasPromo: boolean }}
 */
export function resolveLignePrixPromo(ligne, opts = {}) {
  const brut = resolveLigneBrutTTC(ligne);
  const reductionOrig = roundMoney(Math.max(0, Number(opts.reductionOrig) || 0));
  const sousTotalOriginal = roundMoney(
    Math.max(0, Number(opts.sousTotalOriginal) || 0)
  );
  if (reductionOrig <= 0 || sousTotalOriginal <= 0 || brut <= 0) {
    return { brut, net: brut, partPromo: 0, hasPromo: false };
  }
  const partPromo = roundMoney(reductionOrig * (brut / sousTotalOriginal));
  const net = roundMoney(Math.max(0, brut - partPromo));
  return {
    brut,
    net,
    partPromo,
    hasPromo: partPromo > 0.005 && Math.abs(brut - net) > 0.005,
  };
}

/**
 * Estime le remboursement Stripe = ancienTotal − nouveauTotal (même logique I1).
 */
export function estimateIndisponibleRefundDelta(commande, ligneId) {
  const lignes = commande?.produits || [];
  const lineAmount = (p) =>
    Number(p.prixFinal ?? p.prixTotal ?? 0) || 0;
  const sousTotalOriginal = roundMoney(
    lignes.reduce((s, p) => s + lineAmount(p), 0)
  );
  const sousTotalRestant = roundMoney(
    lignes
      .filter((p) => p.id !== ligneId && p.indisponibleAt == null)
      .reduce((s, p) => s + lineAmount(p), 0)
  );
  const frais = roundMoney(Math.max(0, Number(commande?.fraisLivraison) || 0));
  const reducOrig = roundMoney(Math.max(0, Number(commande?.reduction) || 0));
  let reducEffective = 0;
  if (reducOrig > 0) {
    if (sousTotalOriginal <= 0) {
      reducEffective = roundMoney(Math.min(reducOrig, sousTotalRestant + frais));
    } else {
      reducEffective = roundMoney(
        Math.min(
          reducOrig * (sousTotalRestant / sousTotalOriginal),
          sousTotalRestant + frais
        )
      );
    }
  }
  const ancienTotal = roundMoney(Number(commande?.total) || 0);
  const nouveauTotal = roundMoney(
    Math.max(0, sousTotalRestant + frais - reducEffective)
  );
  return {
    ancienTotal,
    nouveauTotal,
    refundDelta: roundMoney(Math.max(0, ancienTotal - nouveauTotal)),
    reductionEffective: reducEffective,
  };
}
